import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const GROUPS = [
  {
    name: "Children & Young People",
    desc: "You are more than your behaviour, and more than what has happened to you. Ngawilyi is a place where what you know matters — where someone walks beside you, listens properly, and helps your knowledge help others.",
    tone: "bg-terracotta text-cream",
    sub: "text-cream/80",
    linkTo: "/programs",
    linkLabel: "Explore programs",
  },
  {
    name: "Parents & Families",
    desc: "Support that doesn't start with judgement. Yarns, practical help, family sessions and someone in your corner when systems feel overwhelming — working toward your family's hopes, not someone else's checklist.",
    tone: "bg-creamdark text-charcoal border border-ochre/25",
    sub: "text-earth/85",
    linkTo: "/request-support",
    linkLabel: "Request support",
  },
  {
    name: "Kinship Carers",
    desc: "Kinship carers carry enormous cultural, family, employment, financial and caring responsibilities — often with little formal support. Ngawilyi gives particular recognition to that load, with practical help (like support after a suspension call) that protects placements and livelihoods.",
    tone: "bg-charcoal text-cream",
    sub: "text-cream/80",
    linkTo: "/programs",
    linkLabel: "See Those Who Know",
  },
  {
    name: "Foster Carers",
    desc: "Support for the relationship between you and the child in your care — understanding behaviour through story and context, without ever positioning carers as therapists.",
    tone: "bg-blush/50 text-charcoal",
    sub: "text-earth/85",
    linkTo: "/programs",
    linkLabel: "See our programs",
  },
  {
    name: "Aboriginal Communities",
    desc: "Ngawilyi is Aboriginal-owned and being built with community, not delivered to it. We support connection back to family, kin, Elders, community, Country and Aboriginal Community Controlled Organisations.",
    tone: "bg-creamdark text-charcoal border border-ochre/25",
    sub: "text-earth/85",
    linkTo: "/community",
    linkLabel: "Community programs",
  },
  {
    name: "Schools & Educators",
    desc: "A relational bridge for students disengaging or repeatedly suspended — education advocacy, alternative engagement and practical support that keeps learning connected to life.",
    tone: "bg-ochre/30 text-charcoal",
    sub: "text-earth/85",
    linkTo: "/referrals",
    linkLabel: "Make a referral",
  },
  {
    name: "Professionals & Services",
    desc: "Not another silo. Ngawilyi works alongside specialist, statutory, clinical and cultural services — one consistent relationship that helps all the other supports hold together.",
    tone: "bg-creamdark text-charcoal border border-ochre/25",
    sub: "text-earth/85",
    linkTo: "/referrals",
    linkLabel: "Referral information",
  },
];

const WhoWeSupport = () => (
  <div data-testid="who-we-support-page">
    <PageHero
      kicker="Who We Support"
      title={"Built around the people\nsystems often talk past."}
      lede="Children and young people, parents, kinship carers, foster carers, Aboriginal communities, schools and the professionals around them — each met with warmth, respect and plain English."
    />
    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {GROUPS.map((g, i) => (
          <Reveal key={g.name} delay={i * 0.06} className={i === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
            <div data-testid={`support-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-[36px] p-9 h-full flex flex-col transition-transform duration-500 hover:-translate-y-2 ${g.tone}`}>
              <span className="font-display italic text-lg opacity-50">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-3xl font-light leading-snug">{g.name}</h3>
              <p className={`mt-4 text-sm leading-relaxed flex-1 ${g.sub}`}>{g.desc}</p>
              <Link
                to={g.linkTo}
                data-testid={`support-link-${i}`}
                className={`group mt-6 inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-widest ${g.tone.includes("text-cream") ? "text-cream" : "text-terracotta"}`}
              >
                {g.linkLabel} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <CTABand
      title="Someone could actually walk alongside you — instead of giving you another appointment."
      primary={{ to: "/referrals", label: "Make a Referral" }}
      secondary={{ to: "/contact", label: "Yarn With Us" }}
    />
  </div>
);

export default WhoWeSupport;
